import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Zap, QrCode, ArrowLeft, Camera, X, Check, SkipForward, Hash, CheckCircle2 } from "lucide-react";

const GRADE_CHIPS = [20, 30, 40, 45, 50, 55, 60, 70, 80];

const TOOL_SETS = {
  position_player: [
    { key: "hit", label: "Hit" },
    { key: "power", label: "Power" },
    { key: "run", label: "Run" },
    { key: "field", label: "Field" },
    { key: "arm", label: "Arm" },
  ],
  pitcher: [
    { key: "fastball", label: "Fastball" },
    { key: "curveball", label: "Breaking Ball" },
    { key: "changeup", label: "Changeup" },
    { key: "control", label: "Control" },
    { key: "command", label: "Command" },
  ],
  catcher: [
    { key: "receiving", label: "Receiving" },
    { key: "catch_throw", label: "Throwing" },
    { key: "arm", label: "Arm / Pop" },
    { key: "hit", label: "Hit" },
    { key: "power", label: "Power" },
  ],
};

const detectArchetype = (pos) => (pos === "P" ? "pitcher" : pos === "C" ? "catcher" : "position_player");

function chipColor(g, active) {
  if (!active) return "bg-white/5 border border-white/15 text-white/70 hover:border-white/50";
  if (g >= 60) return "bg-[#E63946] text-white border border-[#E63946] ring-2 ring-[#E63946]/40";
  if (g >= 50) return "bg-emerald-400 text-[#0A192F] border border-emerald-400 ring-2 ring-emerald-400/40";
  if (g >= 45) return "bg-amber-400 text-[#0A192F] border border-amber-400 ring-2 ring-amber-400/40";
  return "bg-slate-300 text-[#0A192F] border border-slate-300 ring-2 ring-slate-300/40";
}

export default function QuickEval() {
  const [step, setStep] = useState("camp"); // camp | work
  const [camps, setCamps] = useState([]);
  const [camp, setCamp] = useState(null);
  const [progress, setProgress] = useState({ total: 0, evaluated: 0, remaining: 0, completion_pct: 0 });
  const [current, setCurrent] = useState(null); // { player, player_number, already_evaluated }
  const [archetype, setArchetype] = useState("position_player");
  const [tools, setTools] = useState({});
  const [ofv, setOfv] = useState(null);
  const [notes, setNotes] = useState("");
  const [numberInput, setNumberInput] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [done, setDone] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    api.get("/evaluator/camps").then(({ data }) => setCamps(data.items || [])).catch(() => {});
  }, []);

  // Deep-link support: /quick-eval?camp={id}&player={id}
  useEffect(() => {
    const campId = params.get("camp");
    const playerId = params.get("player");
    if (!campId) return;
    (async () => {
      try {
        const { data } = await api.get(`/evaluator/camps/${campId}/progress`);
        setCamp(data.camp);
        setProgress({ total: data.total, evaluated: data.evaluated, remaining: data.remaining, completion_pct: data.completion_pct });
        setStep("work");
        if (playerId) {
          const { data: pd } = await api.get(`/evaluator/camps/${campId}/resolve`, { params: { player_id: playerId } });
          applyCurrent(pd);
        } else {
          await loadNext(campId);
        }
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Could not open camp");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProgress = useCallback(async (campId) => {
    const { data } = await api.get(`/evaluator/camps/${campId}/progress`);
    setProgress({ total: data.total, evaluated: data.evaluated, remaining: data.remaining, completion_pct: data.completion_pct });
  }, []);

  const applyCurrent = (payload) => {
    setCurrent(payload);
    setArchetype(detectArchetype(payload.player?.primary_position));
    setTools({});
    setOfv(null);
    setNotes("");
    setDone(false);
  };

  const loadNext = useCallback(async (campId, afterNumber) => {
    const { data } = await api.get(`/evaluator/camps/${campId}/next`, { params: afterNumber != null ? { after_number: afterNumber } : {} });
    if (data.done || !data.player) {
      setCurrent(null);
      setDone(true);
    } else {
      applyCurrent(data);
    }
  }, []);

  const selectCamp = async (c) => {
    setCamp(c);
    setStep("work");
    await loadProgress(c.id);
    await loadNext(c.id);
  };

  const resolveByNumber = async () => {
    const n = parseInt(numberInput, 10);
    if (Number.isNaN(n)) { toast.error("Enter a player number"); return; }
    try {
      const { data } = await api.get(`/evaluator/camps/${camp.id}/resolve`, { params: { number: n } });
      applyCurrent(data);
      setNumberInput("");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Player not found"); }
  };

  const resolveByToken = async (text) => {
    try {
      const { data } = await api.get(`/evaluator/camps/${camp.id}/resolve`, { params: { token: text } });
      applyCurrent(data);
      toast.success("Player loaded from QR");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Invalid QR code"); }
  };

  const saveAndNext = useCallback(async () => {
    if (!current?.player || saving) return;
    if (ofv == null) { toast.error("Set an OFV grade before saving"); return; }
    setSaving(true);
    try {
      await api.post("/evaluations", {
        player_id: current.player.id,
        camp_id: camp.id,
        role_archetype: archetype,
        position_evaluated: current.player.primary_position || null,
        tools,
        overall_future_value: ofv,
        written_report: notes || null,
        status: "submitted",
      });
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1100);
      await loadProgress(camp.id);
      await loadNext(camp.id, current.player_number);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  }, [current, saving, ofv, camp, archetype, tools, notes, loadProgress, loadNext]);

  // Keyboard: Enter = Save & Next (ignore when typing in inputs/textarea)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Enter") return;
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "textarea" || tag === "input") return;
      e.preventDefault();
      saveAndNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saveAndNext]);

  if (step === "camp") {
    return (
      <Shell>
        <div className="flex items-center gap-2 label-overline text-[#E63946]"><Zap className="w-4 h-4" /> Quick Evaluation Mode</div>
        <h1 className="font-display text-4xl uppercase tracking-tighter mt-2 text-white">Pick a Camp</h1>
        <p className="text-white/60 text-sm mt-1">Scan QR or enter a player number, grade, save & next — built for sub-60-second evals.</p>
        <div className="mt-6 space-y-3">
          {camps.map((c) => (
            <button key={c.id} data-testid={`quick-camp-${c.id}`} onClick={() => selectCamp(c)}
              className="w-full text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-sm p-4 transition-colors">
              <div className="font-display text-xl uppercase tracking-tight text-white">{c.title}</div>
              <div className="text-xs text-white/50 mt-1">{[c.city, c.province].filter(Boolean).join(", ")} · {c.roster_count} registered</div>
            </button>
          ))}
          {camps.length === 0 && <p className="text-white/50 text-sm">No camps with registered players yet.</p>}
        </div>
        <Link to="/evaluator" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-xs uppercase tracking-wider mt-8">
          <ArrowLeft className="w-4 h-4" /> Back to console
        </Link>
      </Shell>
    );
  }

  return (
    <Shell wide>
      {/* Header / progress */}
      <div className="flex items-center justify-between gap-3">
        <button onClick={() => { setStep("camp"); setCurrent(null); }} className="inline-flex items-center gap-2 text-white/50 hover:text-white text-xs uppercase tracking-wider">
          <ArrowLeft className="w-4 h-4" /> Camps
        </button>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-white/40">{camp?.title}</div>
          <div className="text-xs text-white/70" data-testid="quick-progress-text">
            <span className="text-emerald-400 font-bold">{progress.evaluated}</span> evaluated ·
            <span className="text-[#E63946] font-bold"> {progress.remaining}</span> remaining ·
            <span className="font-bold"> {progress.completion_pct}%</span>
          </div>
        </div>
      </div>
      <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-400 transition-all duration-300" style={{ width: `${progress.completion_pct}%` }} data-testid="quick-progress-bar" />
      </div>

      {/* Lookup controls */}
      <div className="mt-4 flex gap-2">
        <div className="flex-1 flex">
          <div className="flex items-center px-3 bg-white/10 rounded-l-sm text-white/50"><Hash className="w-4 h-4" /></div>
          <input
            data-testid="quick-number-input"
            value={numberInput}
            onChange={(e) => setNumberInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && resolveByNumber()}
            inputMode="numeric"
            placeholder="Player #"
            className="flex-1 bg-white/5 border border-white/10 text-white px-3 py-2.5 text-sm placeholder:text-white/30 focus:outline-none"
          />
          <button data-testid="quick-number-go" onClick={resolveByNumber} className="bg-[#0A192F] border border-white/10 text-white px-4 text-xs font-semibold uppercase tracking-wider rounded-r-sm">Go</button>
        </div>
        <button data-testid="quick-scan-btn" onClick={() => setScanOpen(true)} className="inline-flex items-center gap-2 bg-[#E63946] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-wider rounded-sm">
          <Camera className="w-4 h-4" /> Scan QR
        </button>
      </div>

      {/* Workspace */}
      {done && !current && (
        <div data-testid="quick-done" className="mt-8 text-center bg-emerald-500/10 border border-emerald-500/30 rounded-sm p-10">
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-400" />
          <h2 className="font-display text-2xl uppercase tracking-tight text-white mt-3">Roster Complete</h2>
          <p className="text-white/60 text-sm mt-1">You've evaluated every player on this camp roster. Use the number/scan above to re-open any player.</p>
        </div>
      )}

      {!current && !done && (
        <div className="mt-8 text-center text-white/50 text-sm">Scan a QR code or enter a player number to begin.</div>
      )}

      {current && (
        <div className="mt-5">
          {/* Player header */}
          <div className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-sm p-4">
            <div className="w-16 h-16 rounded-sm bg-white/10 overflow-hidden shrink-0">
              {current.player.profile_photo_url
                ? <img src={current.player.profile_photo_url} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full grid place-items-center text-white/30 text-2xl font-display">{(current.player.first_name || "?")[0]}</div>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-2xl uppercase tracking-tight text-white truncate" data-testid="quick-player-name">{current.player.first_name} {current.player.last_name}</div>
              <div className="text-xs text-white/60 mt-0.5">{[current.player.primary_position, `'${String(current.player.graduation_year || "").slice(-2)}`, current.player.province].filter(Boolean).join(" · ")}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[9px] uppercase tracking-wider text-white/40">Player</div>
              <div className="font-data text-4xl text-[#E63946] leading-none" data-testid="quick-player-number">{current.player_number != null ? String(current.player_number).padStart(3, "0") : "—"}</div>
            </div>
          </div>
          {current.already_evaluated && (
            <div className="mt-2 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-sm px-3 py-1.5" data-testid="quick-already-evaluated">
              You already submitted an evaluation for this player — saving will add another.
            </div>
          )}

          {/* Tool chips */}
          <div className="mt-5 space-y-4">
            {TOOL_SETS[archetype].map((t) => (
              <ChipRow key={t.key} label={t.label} value={tools[t.key]} testid={`quick-tool-${t.key}`}
                onPick={(g) => setTools((prev) => ({ ...prev, [t.key]: prev[t.key] === g ? undefined : g }))} />
            ))}
          </div>

          {/* OFV */}
          <div className="mt-5 border-l-4 border-[#E63946] bg-white/5 p-3 rounded-r-sm">
            <ChipRow label="Overall Future Value (OFV)" value={ofv} testid="quick-ofv" big
              onPick={(g) => setOfv((prev) => (prev === g ? null : g))} />
          </div>

          {/* Quick notes */}
          <textarea
            data-testid="quick-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows="2"
            placeholder="Quick notes (optional)…"
            className="mt-4 w-full bg-white/5 border border-white/10 text-white px-3 py-2 text-sm placeholder:text-white/30 rounded-sm focus:outline-none"
          />

          {/* Save & Next */}
          <button
            data-testid="quick-save-next"
            onClick={saveAndNext}
            disabled={saving}
            className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-4 text-base font-bold uppercase tracking-wider rounded-sm transition-colors disabled:opacity-60 ${justSaved ? "bg-emerald-500 text-white" : "bg-[#E63946] hover:bg-[#D90429] text-white"}`}
          >
            {justSaved ? <><Check className="w-5 h-5" /> Saved</> : saving ? "Saving…" : <><SkipForward className="w-5 h-5" /> Save &amp; Next</>}
          </button>
          <p className="text-center text-[10px] uppercase tracking-wider text-white/30 mt-2">Press Enter to save &amp; load next player</p>
        </div>
      )}

      {scanOpen && (
        <QrScanner
          onResult={(text) => { setScanOpen(false); resolveByToken(text); }}
          onClose={() => setScanOpen(false)}
        />
      )}
    </Shell>
  );
}

function ChipRow({ label, value, onPick, testid, big }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <span className={`uppercase tracking-wider font-semibold text-white ${big ? "text-sm" : "text-xs"}`}>{label}</span>
        {value != null && <span className="font-data text-white/80 text-sm">{value}</span>}
      </div>
      <div className="grid grid-cols-9 gap-1.5">
        {GRADE_CHIPS.map((g) => (
          <button
            key={g}
            data-testid={`${testid}-${g}`}
            onClick={() => onPick(g)}
            className={`py-2.5 ${big ? "text-base" : "text-sm"} font-data font-bold rounded-sm transition-colors ${chipColor(g, value === g)}`}
          >
            {g}
          </button>
        ))}
      </div>
    </div>
  );
}

function QrScanner({ onResult, onClose }) {
  const startedRef = useRef(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const scanner = new Html5Qrcode("qr-reader");
    let stopped = false;
    scanner
      .start({ facingMode: "environment" }, { fps: 10, qrbox: 240 },
        (decodedText) => {
          if (stopped) return;
          stopped = true;
          scanner.stop().catch(() => {}).finally(() => onResult(decodedText));
        },
        () => {})
      .then(() => { startedRef.current = true; })
      .catch(() => setErr("Could not access camera. Check permissions, or use player-number entry."));
    return () => {
      if (startedRef.current && !stopped) scanner.stop().catch(() => {});
    };
  }, [onResult]);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-4" data-testid="quick-scanner-modal">
      <div className="bg-[#0A192F] border border-white/10 rounded-sm p-4 w-full max-w-sm">
        <div className="flex items-center justify-between mb-3">
          <span className="label-overline text-[#E63946] flex items-center gap-2"><QrCode className="w-4 h-4" /> Scan Player QR</span>
          <button data-testid="quick-scanner-close" onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div id="qr-reader" className="overflow-hidden rounded-sm bg-black" />
        {err && <p className="text-amber-300 text-xs mt-3">{err}</p>}
        <p className="text-white/40 text-[11px] mt-3">Point the camera at the player's credential QR code.</p>
      </div>
    </div>
  );
}

function Shell({ children, wide }) {
  return (
    <div className="min-h-screen bg-[#0A192F] py-8 px-4">
      <div className={`mx-auto ${wide ? "max-w-xl" : "max-w-md"}`}>{children}</div>
    </div>
  );
}
